import React, { useEffect } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import loadable from '@loadable/component';
import { Loader } from '@perxis-auto/ui-kit/lib/components/Loader';

import { EVENT_WIDGET_INITIALIZED } from '@/constants/events';
import PAGES from '@/constants/pages';
import { useAppSelector } from '@/features/hooks';
import useFilterSettings from '@/features/installation/hooks/useFilterSettings';
import useRedirectToModels from '@/features/installation/hooks/useRedirectToModels';
import { rootSelectors } from '@/features/store';
import ConstructorLayout from '@/layouts/Constructor';
import sendEvent from '@/utils/sendEvent';

import '@perxis-auto/ui-kit/lib/main.css';

const defaultLoadableOptions = {
  fallback: <Loader className="main-loader" size="large" />,
};

const Car = loadable(() => import('@/pages/car'), defaultLoadableOptions);
const Cars = loadable(() => import('@/pages/cars'), defaultLoadableOptions);
const Models = loadable(() => import('@/pages/models'), defaultLoadableOptions);
const Error = loadable(() => import('@/pages/error'), defaultLoadableOptions);

const PAGE_ELEMENTS_MAP: Record<keyof typeof PAGES, React.ReactNode> = {
  Cars: <Cars />,
  CarId: <Car />,
  Models: <Models />,
  404: <Error statusCode={404} />,
  500: <Error statusCode={500} />,
};

function Pages() {
  const { pathname } = useLocation();

  const availablePagesMap = useAppSelector(
    rootSelectors.config.availablePagesMapSelector,
  );

  useFilterSettings();
  useRedirectToModels();

  useEffect(() => {
    sendEvent(EVENT_WIDGET_INITIALIZED);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return (
    <Routes>
      <Route element={<ConstructorLayout />}>
        {Object.entries(PAGE_ELEMENTS_MAP).map(([path, element]) => {
          const pathKey = path as keyof typeof PAGES;
          const isAvailable = availablePagesMap[pathKey];
          return isAvailable ? (
            <Route key={path} path={PAGES[pathKey]} element={element} />
          ) : null;
        })}
        <Route path="*" element={<Navigate to={PAGES[404]} replace />} />
      </Route>
    </Routes>
  );
}

export default React.memo(Pages);
